import React, { ChangeEventHandler, FC, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import { StickyContainer } from 'react-sticky';
import type { ProductComparisonType } from '../../types/product-comparison';
import Container from '../container';
import InputSelect from '../controls/input-select';
import { Grid, Paragraph } from '../UIKit/system';
import {
  CompareGrid,
  CompareRow,
  ColoursContainer,
  ColourDot,
  Description,
  HeadingSection,
  HeadingDescription,
  GroupsGrid,
  ItemDescription,
  ItemBlock,
  QuoteBtnContainer,
  StickyGrid,
  StickyContent,
  AccordionFirst,
  Accordion,
  ToggleColumnsWrap
} from './ui';
import Title from '../title';
import LazyImage from '../lazy-image';
import Toggle from '../toggle';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import Button from '../button';
import { CompareItemGroupType } from '../../types/compare-item-group';
import { CompareImage } from './compare-image';
import Icon from '../icon';
import RichTextField from '../rich-text-field';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import { getPath } from '../../utils/paths';

enum Selection {
  Col1 = 'COL_1',
  Col2 = 'COL_2',
  Col3 = 'COL_3'
}

interface InputSelectOption {
  name: string;
  value?: string;
  values?: {
    name: string;
    value: string;
  }[];
}

const selectionValues = Object.values(Selection);

export const ProductComparison: FC<ProductComparisonType> = (props) => {
  const {
    heading,
    description,
    headingSignpost,
    isMainHeading,
    compare,
    columnSwitchOnLabel,
    columnSwitchOffLabel
  } = props;
  const [toggleColumns, setToggleColumns] = useState(true);
  const isTwoColumns = compare.length == 2;
  const twoColumns = isTwoColumns || !toggleColumns;

  const allOptions = useMemo(() => {
    return compare.reduce((items, item) => {
      if (item.group) {
        const itemGroup = item.group?.trim();
        const existingItemIndex = items.findIndex(
          (sItem) => sItem.name === itemGroup
        );

        if (existingItemIndex !== -1) {
          items[existingItemIndex].values?.push({
            name: item.title,
            value: item._uid
          });
        } else {
          items.push({
            name: itemGroup,
            values: [
              {
                name: item.title,
                value: item._uid
              }
            ]
          });
        }

        return items;
      }

      items.push({
        name: item.title,
        value: item._uid
      });

      return items;
    }, [] as InputSelectOption[]);
  }, [compare]);

  const [selection, setSelection] = useState({
    [Selection.Col1]: compare[0]?._uid ?? '',
    [Selection.Col2]: compare[1]?._uid ?? '',
    [Selection.Col3]: compare[2]?._uid ?? ''
  });

  const hasSelection =
    selection[Selection.Col1] ||
    selection[Selection.Col2] ||
    selection[Selection.Col3];

  const uniqueGroups = useMemo(() => {
    return uniqBy(
      compare
        .map((item) => item.data.map((dataItem) => dataItem.group))
        .flat()
        .filter((group): group is NonNullable<typeof group> => !!group),
      'uuid'
    );
  }, [compare]);

  const selectedItems = useMemo(() => {
    return [
      compare.find((item) => item._uid === selection.COL_1),
      compare.find((item) => item._uid === selection.COL_2),
      compare.find((item) => item._uid === selection.COL_3)
    ];
  }, [compare, selection]);

  const onUpdateSelection: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const { name, value } = event.target;

    setSelection((current) => {
      const next = { ...current, [name]: value };
      // If the new value duplicates another column, replace that column with the previous value of this column
      for (const col of selectionValues) {
        if (col !== name && next[col] === value) {
          next[col] = current[name as keyof typeof current];
        }
      }
      return next;
    });
  };

  const renderInfo = () => {
    return (
      <>
        <CompareRow twoColumns={twoColumns}>
          {selectedItems.map((item, index) => (
            <div key={`image-${item?._uid || index}`}>
              {item && (
                <CompareImage
                  image={item.image}
                  secondImage={item.secondaryImage}
                />
              )}
            </div>
          ))}
        </CompareRow>

        <CompareRow twoColumns={twoColumns}>
          {selectedItems.map((item, index) => (
            <div key={`description-${item?._uid || index}`}>
              {item && <Description>{item.description}</Description>}
            </div>
          ))}
        </CompareRow>

        <CompareRow twoColumns={twoColumns}>
          {selectedItems.map((item, index) => (
            <div key={`link-${item?._uid || index}`}>
              {item && (
                <Button to={parseStoryblokLink(item.link)}>Learn more</Button>
              )}
            </div>
          ))}
        </CompareRow>

        <CompareRow twoColumns={twoColumns}>
          {selectedItems.map((item, index) => (
            <QuoteBtnContainer key={`quote-button-${item?._uid || index}`}>
              {item?.showGetQuote && (
                <Button
                  to={`${getPath.contact()}?${new URLSearchParams({
                    message: `Please quote for: ${item.title}`
                  })}`}
                  borderGrey
                >
                  Get a quote
                </Button>
              )}
            </QuoteBtnContainer>
          ))}
        </CompareRow>
      </>
    );
  };

  const renderItemLinkBySelection = (selection: string) => {
    const item = selectedItems.find((item) => item?._uid === selection);

    if (!item) return null;

    return <Button to={parseStoryblokLink(item.link)}>Learn more</Button>;
  };

  const renderInfoGroup = (group: CompareItemGroupType) => {
    return (
      <CompareRow largeRowGap twoColumns={twoColumns}>
        {selectedItems.map((item, colIndex) =>
          item?.data
            .find((section) => section.group?.uuid === group.uuid)
            ?.items.map((dataItem, rowIndex) => {
              return (
                <ItemBlock
                  key={dataItem._uid}
                  gridColumn={colIndex + 1}
                  gridRow={rowIndex + 1}
                  twoColumns={twoColumns}
                >
                  {dataItem.image?.filename && (
                    <Grid justifyContent={dataItem.centerImage && 'center'}>
                      <LazyImage
                        url={
                          dataItem.image?.filename.endsWith('.svg')
                            ? dataItem.image?.filename
                            : undefined
                        }
                        fluid={getFluidGatsbyImage(dataItem.image)}
                        height={50}
                        width={50}
                      />
                    </Grid>
                  )}

                  {Array.isArray(dataItem.colours) &&
                    dataItem.colours.length > 0 && (
                      <ColoursContainer>
                        {dataItem.colours.map((color) => (
                          <ColourDot key={color} backgroundColor={color} />
                        ))}
                      </ColoursContainer>
                    )}

                  {dataItem.description && (
                    <ItemDescription center={dataItem.centerImage}>
                      <RichTextField data={dataItem.description} />
                    </ItemDescription>
                  )}
                </ItemBlock>
              );
            })
        )}
      </CompareRow>
    );
  };

  return (
    <Container large>
      {heading && (
        <HeadingSection>
          <Title as={isMainHeading ? 'h1' : 'h2'} textAlign="center">
            {headingSignpost}
            <Paragraph
              as="span"
              color="vividPrimary"
              fontSize="inherit"
              lineHeight="inherit"
              display="block"
            >
              {heading}
            </Paragraph>
          </Title>
          {description && (
            <HeadingDescription>{description}</HeadingDescription>
          )}

          {compare.length > 2 && (
            <ToggleColumnsWrap>
              <Toggle
                name="toggle-columns"
                checked={toggleColumns}
                onChange={setToggleColumns}
              />
              <label htmlFor="toggle-columns">
                {toggleColumns ? columnSwitchOnLabel : columnSwitchOffLabel}
              </label>
            </ToggleColumnsWrap>
          )}
        </HeadingSection>
      )}

      <StickyContainer>
        <Grid>
          <StickyGrid>
            <CompareRow twoColumns={twoColumns}>
              {selectionValues.map((selectionValue) => (
                <div key={`dropdown-${selectionValue}`}>
                  <InputSelect
                    field={{
                      value: selection[selectionValue],
                      name: selectionValue,
                      onChange: onUpdateSelection
                    }}
                    options={allOptions}
                    showNone={false}
                    whiteBordered
                    boldText
                  />

                  <StickyContent>
                    {renderItemLinkBySelection(selection[selectionValue])}
                  </StickyContent>
                </div>
              ))}
            </CompareRow>
          </StickyGrid>

          <CompareGrid paddingTop={[0, 8]} paddingBottom={[40, 68]}>
            {renderInfo()}
          </CompareGrid>

          {hasSelection && (
            <GroupsGrid>
              {uniqueGroups.map((group, index) => {
                const AccordionWrapper =
                  index === 0 ? AccordionFirst : Accordion;

                return (
                  <AccordionWrapper
                    key={group.uuid}
                    title={group.name}
                    narrow={twoColumns}
                  >
                    {renderInfoGroup(group)}
                  </AccordionWrapper>
                );
              })}
            </GroupsGrid>
          )}
        </Grid>
      </StickyContainer>
    </Container>
  );
};
